<template>
  <v-card class="step-tab-panel" flat>
    <v-container class="pt-3 pa-0 ma-0">
      <v-alert v-if="unscopedMappings.length" variant="tonal" class="mb-1">
        {{ tkey('checkSuggestedSelectionsMessage') }}
      </v-alert>
    </v-container>

    <div class="h-100 flex-grow-1">
      <AgGridVue
        class="h-100 w-100"
        :column-defs="columnDefs"
        :row-data="clusteredFurnitureData"
        :grid-options="gridOptions"
        :stop-editing-when-grid-loses-focus="true"
        @cell-value-changed="trackDiff"
        @grid-ready="onGridReady"
      />
    </div>

    <PageActions
      :has-data-changes="!isSaveDisabled"
      :has-data-errors="hasDataErrors"
      :is-discard-enabled="hasDataChanges"
      @discard="discardChanges"
      @save="saveChanges"
    />

    <UnsavedDataModal
      ref="unsavedDataModal"
      :value="isUnsavedDataModalOpen"
      @cancel="closeUnsavedDataModal"
      @confirm="confirmUnsavedDataModal"
    />
  </v-card>
</template>

<script>
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFurnitureStore } from '@/stores/furniture';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import agGridUtils from '@/utils/ag-grid-utils';
import {
  cloneDeep,
  flatten,
  forEach,
  get,
  groupBy,
  isEmpty,
  isNull,
  keyBy,
  orderBy,
  reduce,
  uniq,
} from 'lodash';
import { mapActions, mapState } from 'pinia';

export default {
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave(to, from, next);
  },
  setup() {
    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    } = useUnsavedDataWarning();
    const { tkey } = useInternationalization('planogramSelectionPage');
    return {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      tkey,
    };
  },
  data() {
    return {
      gridApi: null,
      columnDefs: [
        {
          headerName: this.tkey('tableHeaders.cluster'),
          field: 'cluster',
          pinned: 'left',
          sort: null,
          valueFormatter: params => {
            return this.isfirstRowOfCluster(params) ? params.value : '';
          },
        },
        {
          headerName: this.tkey('tableHeaders.storeClass'),
          field: 'storeClass',
        },
        {
          headerName: this.tkey('tableHeaders.furnitures'),
          field: 'furnitureName',
          colSpan: params => {
            return params.data.isFooter ? 2 : 1;
          },
          cellClass: params => {
            if (params.data.isFooter) return ['pr-4', 'justify-end'];
          },
          flex: 1,
        },
        {
          headerName: this.tkey('tableHeaders.numberOfProducts'),
          headerClass: 'border-right',
          cellClass: 'border-right',
          field: 'numberOfProducts',
        },
        {
          headerName: this.tkey('tableHeaders.numberOfStores'),
          headerClass: 'border-right',
          cellClass: 'border-right',
          field: 'numberOfStores',
        },
        {
          headerName: this.tkey('tableHeaders.inScope'),
          headerClass: 'border-right justify-center',
          cellClass: 'border-right',
          field: 'inScope',
          menuTabs: ['generalMenuTab', 'filterMenuTab'],
          valueParser: agGridUtils.parsers.booleanParser,
          cellRenderer: params => {
            if (!params.data.isFooter) {
              return agGridUtils.utils.checkboxRenderer(params, this.handleCheckboxChange);
            }
          },
          cellRendererParams: { field: 'inScope' },
          editable: params => {
            return !params.data.isFooter;
          },
        },
      ],
      gridOptions: {
        columnMenu: 'legacy',
        cellSelection: { handle: { mode: 'range' } },
        suppressContextMenu: true,
        headerHeight: 40,
        defaultColDef: {
          filter: true,
          sortable: true,
          resizable: true,
          minWidth: 80,
          comparator: agGridUtils.sortings.naturalSort,
          editable: false,
          menuTabs: ['filterMenuTab'],
          suppressMovable: true,
          cellClassRules: { 'diff-background': this.hasDiff },
          suppressKeyboardEvent: agGridUtils.utils.suppressKeyboardEvent,
        },
        rowClassRules: {
          'cluster-start-row': params => this.isfirstRowOfCluster(params),
          'footer-row': params => params.data.isFooter,
        },
        postSortRows: this.postSort,
        processCellFromClipboard: agGridUtils.utils.processCellFromClipboard,
        getMainMenuItems: params =>
          agGridUtils.utils.toggleAllMenuItems(params, this.inScopeToggleAll),
      },
      currentStateDiff: {},
      clusteredFurnitureData: null,
      defaultScope: true,
      unscopedMappings: [],
      footerRowData: null,
    };
  },

  computed: {
    ...mapState(useFurnitureStore, ['getSimpleSwapsClusteredFurnitureMapping']),

    clusterRows() {
      if (isEmpty(this.gridApi)) return [];

      const clusterRows = [];
      this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
        if (index === 0) {
          clusterRows.push(node);
        } else {
          const previousClusterRow = clusterRows.slice(-1)[0];
          if (previousClusterRow.data.cluster !== node.data.cluster) {
            clusterRows.push(node);
          }
        }
      });

      return clusterRows;
    },

    isSaveDisabled() {
      // This lets you save the default data
      if (this.unscopedMappings.length) return false;

      return !this.hasDataChanges;
    },

    hasDataChanges() {
      return !isEmpty(this.currentStateDiff);
    },

    hasDataErrors() {
      // required for <page-actions> component
      return false;
    },
  },

  async created() {
    await this.init();
  },

  methods: {
    ...mapActions(useFurnitureStore, [
      'fetchFurnitureProductsByWorkpackage',
      'saveScenarioFurniture',
    ]),
    ...mapActions(useSnackbarStore, ['showWarning']),
    ...mapActions(useScenariosStore, ['refreshScenario']),

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.redrawRows(); // force clusterRows to evaluate after gridApi is set
      if (this.footerRowData) this.gridApi.setGridOption('pinnedBottomRowData', this.footerRowData); // force set bottom row data in case onGridReady fired after init
    },

    async init() {
      await this.fetchFurnitureProductsByWorkpackage();
      this.clusteredFurnitureData = this.getClusteredFurnitureData();
      if (!isEmpty(this.gridApi))
        this.gridApi.setGridOption('pinnedBottomRowData', this.footerRowData); // if onGridReady already fired, set bottom row data

      // Sets default inScope value for clustered furniture if missing
      this.unscopedMappings = this.clusteredFurnitureData.filter(cf => isNull(cf.inScope));
      if (this.unscopedMappings.length) {
        this.unscopedMappings.forEach(f => {
          f.inScope = this.defaultScope;
        });
      }

      // backup data
      this.currentStateDiff = {};
      this.$options.clusteredFurnitureData = cloneDeep(this.clusteredFurnitureData);
      this.$options.savedState = this.formatSavedStateData(this.$options.clusteredFurnitureData);
    },

    getClusteredFurnitureData() {
      const tableRows = [];
      const furnitureStoreKeys = [];

      forEach(this.getSimpleSwapsClusteredFurnitureMapping, f => {
        const {
          cluster,
          storeClass,
          furnitureId,
          furnitureName,
          numberOfProducts,
          numberOfStores,
        } = f;
        tableRows.push({
          cluster,
          storeClass,
          furnitureId,
          furnitureName,
          numberOfProducts,
          numberOfStores,
          inScope: f.inScope,
        });

        furnitureStoreKeys.push(f.storeKeys);
      });

      // Define footer row data
      this.footerRowData = [
        {
          cluster: null,
          storeClass: null,
          furnitureName: this.tkey('totalStores'),
          numberOfProducts: null,
          numberOfStores: uniq(flatten(furnitureStoreKeys)).length,
          inScope: null,
          isFooter: true,
        },
      ];

      return tableRows;
    },

    inScopeToggleAll(colId, value) {
      this.clusteredFurnitureData.forEach(f => {
        const { cluster, furnitureId } = f;
        if (!cluster) return;

        // Update table data and current state with updated vales
        f[colId] = value;
        this.updateCurrentState(cluster, furnitureId, colId, value);
      });

      // Refresh table cells to see changes
      const { columns } = colId;
      this.gridApi.refreshCells({ columns });
    },

    isfirstRowOfCluster(params) {
      return this.clusterRows.map(v => v.id).includes(get(params, 'node.id'));
    },

    postSort({ rowNodes }) {
      // runs before onGridReady, check gridApi
      if (isEmpty(rowNodes) || isEmpty(this.gridApi)) return;

      const sortDirection = this.gridApi.getColumn('cluster').sort;
      const orderedNodes = orderBy(rowNodes, [n => n.data.cluster], [sortDirection]);

      // have to use splice to update reference to rowNodes. see https://www.ag-grid.com/javascript-grid-sorting/#post-sort
      rowNodes.splice(0, rowNodes.length, ...orderedNodes);

      // need to redraw rows to get correct styling for groups
      this.gridApi.redrawRows({ rowNodes });
      return rowNodes;
    },

    handleCheckboxChange(params) {
      params.setValue(!params.value);
    },

    trackDiff(params) {
      const { cluster, furnitureId } = params.data;
      const { field } = params.colDef;

      this.updateCurrentState(cluster, furnitureId, field, params.value);
    },

    hasDiff(params) {
      const { cluster, furnitureId, isFooter } = params.data;
      const { field } = params.colDef;
      const path = `${cluster}.${furnitureId}.${field}`;

      const currentValue = params.value;
      const originalValue = get(this.$options.savedState, path);

      // Will only check difference for editable cells
      return !isFooter && agGridUtils.comparators.didValueChange(currentValue, originalValue);
    },

    updateCurrentState(cluster, furnitureId, field, currentValue) {
      const path = `${cluster}.${furnitureId}.${field}`;
      const originalValue = get(this.$options.savedState, path);
      const existingValue = get(this.currentStateDiff, [cluster, furnitureId]);

      // If the current state does not match the original, track the state difference
      if (agGridUtils.comparators.didValueChange(currentValue, originalValue)) {
        if (!this.currentStateDiff[cluster]) {
          this.currentStateDiff[cluster] = {};
        }
        if (!this.currentStateDiff[cluster][furnitureId]) {
          this.currentStateDiff[cluster][furnitureId] = {};
        }
        this.currentStateDiff[cluster][furnitureId][field] = currentValue;
      } else if (existingValue) {
        delete this.currentStateDiff[cluster][furnitureId][field];

        // If there is nothing left in the object, remove it
        if (isEmpty(this.currentStateDiff[cluster][furnitureId])) {
          delete this.currentStateDiff[cluster][furnitureId];
        }

        if (isEmpty(this.currentStateDiff[cluster])) {
          delete this.currentStateDiff[cluster];
        }
      }
    },

    prepareFurnitureSelectionsForSave() {
      return this.clusteredFurnitureData
        .filter(furniture => furniture.cluster)
        .map(f => {
          const { cluster, furnitureId, inScope } = f;
          return {
            cluster,
            furnitureId,
            inScope,
          };
        });
    },

    formatSavedStateData(data) {
      const validClusters = data.filter(furniture => furniture.cluster);
      const groupedClusteredData = groupBy(validClusters, 'cluster');

      return reduce(
        groupedClusteredData,
        (acc, cluster, index) => {
          acc[index] = keyBy(cluster, 'furnitureId');
          return acc;
        },
        []
      );
    },

    async saveChanges() {
      try {
        await this.saveScenarioFurniture({
          simpleSwapsFurnitureSelection: this.prepareFurnitureSelectionsForSave(),
        });

        this.$options.clusteredFurnitureData = cloneDeep(this.clusteredFurnitureData);
        this.$options.savedState = this.formatSavedStateData(this.$options.clusteredFurnitureData);

        this.gridApi.refreshCells({ columns: ['inScope'] });
        this.unscopedMappings = [];
        this.resetChanges();
        await this.refreshScenario();
      } catch (e) {
        this.showWarning();
      }
    },

    discardChanges() {
      this.clusteredFurnitureData = cloneDeep(this.$options.clusteredFurnitureData);
      this.resetChanges();
    },

    resetChanges() {
      this.currentStateDiff = {};
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.v-alert) {
  background: $assortment-warning-alert-background !important;
  border: 1px solid $assortment-warning-alert-border !important;
  border-radius: 0 !important;
  padding: 14px;

  &:before {
    content: none;
  }

  .v-alert__content {
    color: $assortment-text-colour;
    font-size: 1.2rem;
    line-height: 1.5rem;
  }
}

:deep(.diff-background) {
  background-color: $assortment-table-changed-cell-bg-colour;
}

:deep(.ag-theme-alpine) {
  .ag-header {
    border-bottom: 1px solid $assortment-grid-line-colour !important;
  }

  .ag-row {
    &.cluster-start-row:not(:first-of-type) {
      border-top: 1px solid $assortment-grid-line-colour !important;
    }
  }

  .border-right {
    border-right: 1px solid $assortment-divider-colour !important;
  }

  .footer-row {
    background-color: $assortment-header-footer-colour;
    .border-right:not(:last-of-type) {
      border-right-color: $assortment-header-footer-colour !important;
    }
    .ag-cell-last-left-pinned {
      border-right: $assortment-header-footer-colour !important;
    }
  }
}
</style>
